import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { exportFieldGroups, exportDefaults } from '../utils/exportSelection';
import type { ExportFieldGroup } from '../utils/exportSelection';
import './Modal.css';

interface ExportFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedFields: string[], format: 'csv' | 'json') => void;
  dataSource: 'people' | 'contacts' | 'company' | 'accounts';
}

const ExportFieldsModal: React.FC<ExportFieldsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  dataSource
}) => {
  const { getSchema, getExportConfig } = useApi();
  
  const [loading, setLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [fieldGroups, setFieldGroups] = useState<ExportFieldGroup[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [defaultFields, setDefaultFields] = useState<string[]>([]);

  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setSearchTerm('');
    setFieldGroups([]);
    setSelectedFields(new Set());
    setDefaultFields([]);
    async function loadFields() {
      try {
        const [schema, config] = await Promise.all([getSchema(dataSource), getExportConfig()]);
        if (cancelled) return;
        if (!schema || !config) throw new Error('Export fields could not be loaded. Close and reopen this dialog to retry.');
        const groups = exportFieldGroups(schema, config, dataSource);
        const defaults = exportDefaults(config, dataSource);
        if (!groups.length) throw new Error('No approved export fields are available.');
        setFieldGroups(groups);
        setDefaultFields(defaults);
        setSelectedFields(new Set(defaults));
      } catch (error) {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Could not load export fields.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadFields();
    return () => { cancelled = true; };
  }, [isOpen, dataSource, getSchema, getExportConfig]);

  const handleFieldToggle = (fieldName: string) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(fieldName)) {
      newSelected.delete(fieldName);
    } else {
      newSelected.add(fieldName);
    }
    setSelectedFields(newSelected);
  };


  const selectAllFields = () => {
    const allFields = fieldGroups.flatMap(group => 
      group.fields.map(field => field.name)
    );
    setSelectedFields(new Set(allFields));
  };

  const selectDefaultFields = () => {
    setSelectedFields(new Set(defaultFields));
  };

  const clearAllFields = () => {
    setSelectedFields(new Set());
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedFields), format);
    onClose();
  };

  const filteredGroups = fieldGroups.map(group => ({
    ...group,
    fields: group.fields.filter(field => 
      field.definition.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(group => group.fields.length > 0);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content export-fields-modal" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Export Fields</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '20px', maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <div className="spinner" />
              <p>Loading...</p>
            </div>
          ) : loadError ? (
            <p role="alert">{loadError}</p>
          ) : (
            <>
              {/* Format & Actions in one row */}
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                    <input
                      type="radio"
                      value="csv"
                      checked={format === 'csv'}
                      onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                    />
                    CSV
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                    <input
                      type="radio"
                      value="json"
                      checked={format === 'json'}
                      onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                    />
                    JSON
                  </label>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={selectAllFields}
                    className="button-secondary"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={selectDefaultFields}
                    className="button-secondary"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Default
                  </button>
                  <button
                    type="button"
                    onClick={clearAllFields}
                    className="button-secondary"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Search */}
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Field Groups */}
              <div className="field-groups">
                {filteredGroups.length > 0 ? (
                  filteredGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="field-group" style={{ marginBottom: '20px' }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#374151',
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '4px'
                      }}>
                        {group.name}
                      </h4>
                      <div className="field-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {group.fields.map((field: any, fieldIndex: number) => (
                          <label
                            key={fieldIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: selectedFields.has(field.name) ? '#f0f9ff' : 'white',
                              transition: 'background-color 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFields.has(field.name)}
                              onChange={() => handleFieldToggle(field.name)}
                              style={{ margin: 0, width: '16px', height: '16px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px' }}>{field.definition.label}</div>
                              <div style={{ color: '#6b7280', fontSize: '12px' }}>{field.name}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#6b7280'
                  }}>
                    {searchTerm ? 'No fields match your search.' : 'No fields available.'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ 
          padding: '20px', 
          background: 'white', 
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="selection-info" style={{ 
            fontSize: '14px', 
            color: '#6b7280', 
            fontWeight: '500' 
          }}>
            <span>{selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
            <button className="button-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button-primary"
              onClick={handleConfirm}
              disabled={selectedFields.size === 0 || loading || !!loadError}
            >
              <Download size={16} style={{ marginRight: '6px' }} />
              Export {selectedFields.size} Field{selectedFields.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportFieldsModal;
